'use es6';

import { createSelector } from 'reselect';
import SeamlessImmutable from 'seamless-immutable';
import { EMPTY_ARRAY, isArrayValuesEqual } from 'DesignData/lib/array';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { getWidgetAncestry, getLayoutData } from 'DesignData/selectors/layoutData';

const getWidgetIdsFromProps = (state, props) => props.widgetIds || EMPTY_ARRAY;

const getIncludeAncestryFromProps = (state, {
  includeAncestry
}) => includeAncestry === !!includeAncestry ? includeAncestry : true;
/**
 * Required props:
 *  - layoutId | assetId
 */


const getByLayout = (state, props) => SeamlessImmutable.getIn(state.unifiedLayouts.cosIndexConnections.byLayout, [props.layoutId || props.assetId], EMPTY_OBJECT);
/**
 * Get COS Index Connections collection by widget.
 *
 * Required props:
 *  - layoutId | assetId
 */


export const getByWidget = (state, props) => getByLayout(state, props).byWidget || EMPTY_OBJECT;
/**
 * Gets widgets where `isContentOverridden` is `true` in `layoutData`
 *
 * Required props:
 *  - layoutId | assetId
 */

export const makeGetContentOverriddenByLayout = () => {
  const DEFAULT_DATA = SeamlessImmutable({
    list: [],
    widgets: {}
  });
  return createSelector([getLayoutData], layoutData => {
    if (!layoutData) return DEFAULT_DATA;
    const {
      widgets
    } = layoutData;
    const data = {
      list: [],
      widgets: {}
    };
    Object.keys(widgets).forEach(widgetId => {
      const widget = widgets[widgetId];

      if (widget.isContentOverridden) {
        data.list.push(widgetId);
        data.widgets[widgetId] = widget;
      }
    });
    return data.list.length ? SeamlessImmutable(data) : DEFAULT_DATA;
  });
};
/**
 * Given a list of widgetIds (list-A), gets a new array of widgetIds (list-B) reflecting
 * those widgets with content that would be affected by changes to widgets in list-A.
 *
 * Required props:
 *  - layoutId | assetId
 *  - widgetIds
 *
 * @returns {Array.<String>}
 */

export const makeReduceWidgetIdsToContentOverridden = () => {
  let result = EMPTY_ARRAY;
  const getContentOverriddenByLayout = makeGetContentOverriddenByLayout();
  return createSelector([getWidgetIdsFromProps, getContentOverriddenByLayout, getWidgetAncestry, getIncludeAncestryFromProps], (widgetIds, contentOverridden, ancestryMap, includeAncestry) => {
    if (!(widgetIds && widgetIds.length)) {
      return result = EMPTY_ARRAY;
    }

    const {
      widgets,
      list
    } = contentOverridden;

    if (!(list && list.length)) {
      return result = EMPTY_ARRAY;
    }

    const iterateAncestry = includeAncestry && ((widgetId, callback) => {
      for (let node = ancestryMap[widgetId]; node; node = node.next) {
        if (node.data.isRoot) break;
        callback(node.data);
      }
    });

    const deletesWithContent = new Set();
    const widgetIdsHash = {};
    widgetIds.forEach(widgetId => {
      widgetIdsHash[widgetId] = widgetId; // Check if requested widget is overridden

      if (widgets[widgetId]) {
        deletesWithContent.add(widgetId);
      } // Check if requested widget is within a group that is overridden.
      // <Widget(overridden)>
      //    <Widget(requested widget) />
      // </Widget>


      if (includeAncestry) {
        iterateAncestry(widgetId, ({
          parentId
        }) => {
          if (widgets[parentId]) {
            deletesWithContent.add(parentId);
          }
        });
      }
    }); // Check for requested widgets with children that are overridden.
    // <Widget(requested widget)>
    //    <Widget(overridden) />
    // </Widget>

    if (includeAncestry) {
      list.forEach(id => {
        iterateAncestry(id, ({
          parentId
        }) => {
          if (widgetIdsHash[parentId]) {
            deletesWithContent.add(id);
          }
        });
      });
    } // Global groups are special in that their content is not stored on pages


    deletesWithContent.forEach(a => {
      if (widgets[a].type === 'global_group') {
        deletesWithContent.delete(a);
      }
    });

    if (!deletesWithContent.size) {
      return result = EMPTY_ARRAY;
    }

    const newResult = Array.from(deletesWithContent).sort();

    if (result === EMPTY_ARRAY || !isArrayValuesEqual(result, newResult)) {
      return result = SeamlessImmutable(newResult);
    }

    return result;
  });
};